import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import styled from 'styled-components';

import { Checkbox } from '@/features/vfo/components';
import { VfoFileIcon } from '@/features/vfo/components/VfoFileIcon';
import { MAX_TASK_FILES } from '@/features/vfo/constants';
import { useCases } from '@/features/vfo/hooks/useCases';
import { useDocuments } from '@/features/vfo/hooks/useDocuments';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { ApiDocumentResponseV2 } from '@/shared/api';
import { breakpoints, media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { Button } from '@/shared/uikitV2/Button';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { SelectDocumentFilesProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../../interwill/containers/ModalContainerV1';

export const SelectDocumentFiles: FC<SelectDocumentFilesProps> = ({
  caseId,
  taskAttachments,
  onSave,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'selectDocumentFiles' });

  const { documents } = useDocuments();
  const { cases } = useCases();
  const { currentUser } = useMembers();

  const [selectedFiles, setSelectedFiles] = useState<ApiDocumentResponseV2[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const modals = useModalsStore();
  const isMaxReached = taskAttachments.length + selectedFiles.length > MAX_TASK_FILES;

  const currentCase = useMemo(() => {
    return cases.find((c) => c.id === caseId);
  }, [cases, caseId]);

  const availableDocuments = useMemo(() => {
    if (!currentCase) return [];
    const documentIds = new Set(currentCase.caseDocuments?.map((doc) => doc.documentId) || []);
    const documentsList = documents.filter(
      (doc) => documentIds.has(doc.id) && !taskAttachments.some((a) => a.id === doc.id),
    );

    return documentsList;
  }, [currentCase, documents]);

  const handleToggle = (doc: ApiDocumentResponseV2) => {
    setSelectedFiles((prev) =>
      prev.some((item) => item.id === doc.id)
        ? prev.filter((m) => m.id !== doc.id)
        : [...prev, doc],
    );
  };

  const handleSave = async () => {
    if (isMaxReached) return;

    setIsLoading(true);
    await onSave(selectedFiles);
    setIsLoading(false);
    modals.closeModal();
  };

  const handleCreate = () => {
    modals.closeModal();

    const type = currentUser?.isFounder ? 'AddVfoDocumentModal' : 'UploadVfoDocumentModal';
    modals.openModal({ type, caseId, onFinish: onSave });
  };

  if (!currentCase) return null;

  return (
    <ModalContainerV1 title={t('title')}>
      <ListContainer>
        <p style={{ fontSize: 14 }}>{t('text', { caseName: currentCase.title })}</p>
        <ItemsList>
          {availableDocuments.map((file) => (
            <ListItem key={file.id} onClick={() => handleToggle(file)}>
              <Checkbox checked={selectedFiles.includes(file)} />
              <FileTypeWrapper className={cn({ isExternal: file.source === 'EXTERNAL' })}>
                <VfoFileIcon file={file} size="s20" />
              </FileTypeWrapper>
              <FileContent>
                {file.isNew && <TextSmall>{t('new')}</TextSmall>}
                <Text>{file.title}</Text>
              </FileContent>
            </ListItem>
          ))}
        </ItemsList>
      </ListContainer>
      <Buttons>
        <Button size="middle" type="text" onClick={handleCreate}>
          {t('addNewBtn')}
        </Button>
        <ActionButtons>
          <Button size="middle" type="default" onClick={modals.closeModal}>
            {t('cancelBtn')}
          </Button>
          <Button
            size="middle"
            onClick={handleSave}
            disabled={!selectedFiles.length || isMaxReached}
            loading={isLoading}
          >
            {t('addBtn')}
          </Button>
        </ActionButtons>
      </Buttons>
    </ModalContainerV1>
  );
};

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  padding: 24px;

  ${media.md}, ${breakpoints.sm} {
    padding: 16px;
  }
`;

const ItemsList = styled.div`
  border-top: 1px solid ${COLORS.systemLight300};
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  height: 64px;
  gap: 8px;
  flex-shrink: 0;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 24px;

  ${media.md}, ${breakpoints.sm} {
    flex-direction: column-reverse;
    gap: 8px;
    margin-top: auto;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  ${media.md}, ${breakpoints.sm} {
    flex-direction: column-reverse;
    align-items: stretch;
  }
`;

const FileTypeWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.systemDark200};
  border-radius: 4px;
  width: 28px;
  height: 36px;
  color: ${COLORS.white};
  position: relative;
  flex-shrink: 0;

  &.isExternal {
    background: ${COLORS.systemSky300};
  }
`;

const Text = styled.span`
  font-size: 12px;
  line-height: 16px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  max-height: 40px;
`;

const FileContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TextSmall = styled.p`
  font-size: 10px;
  line-height: 12px;
  font-weight: 700;
  color: ${COLORS.systemSky200};
`;
